import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Textarea } from 'components/common';
import RichTextEditor from 'components/admin/RichTextEditor';
import { FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import {
    createCustomPage,
    getAdminCustomPageById,
    updateCustomPage
} from 'apis/adminCustomPages';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

// Utility hàm slugify tiếng Việt
const slugify = (text = '') => {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/[^a-z0-9\s-]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
};

const CustomPagesForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [isSlugManuallyEdited, setIsSlugManuallyEdited] = useState(false);
    const [formData, setFormData] = useState({
        slug: '',
        sortOrder: 0,
        isActive: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: detail, error: detailError } = useAdminDetailQuery(
        'custom-pages',
        id,
        getAdminCustomPageById
    );
    const createMutation = useAdminCreateMutation('custom-pages', createCustomPage);
    const updateMutation = useAdminUpdateMutation('custom-pages', updateCustomPage);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length && (!formData.translation || formData.translation.length === 0)) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({
                    languageId: l.id,
                    title: '',
                    slug: '',
                    summary: '',
                    content: '',
                    metaTitle: '',
                    metaDescription: '',
                }))
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            setFormData({
                slug: detail.slug || '',
                sortOrder: detail.sortOrder || 0,
                isActive: detail.isActive ?? true,
                translation: detail.translation || detail.translations || [],
            });
            setIsSlugManuallyEdited(true);
        }
    }, [detail]);

    useEffect(() => {
        if (detailError) toast.error(detailError.message || 'Tải dữ liệu trang thất bại');
    }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'slug') {
            setIsSlugManuallyEdited(true);
        }
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => {
            const updatedTranslations = prev.translation.map(t => {
                if (t.languageId !== langId) return t;
                const updated = { ...t, [field]: value };
                // Tự động sinh translation slug nếu đang sửa title và slug chưa nhập
                if (field === 'title' && (!t.slug || t.slug === slugify(t.title))) {
                    updated.slug = slugify(value);
                }
                return updated;
            });

            // Nếu sửa title ở tab đầu tiên (mặc định Tiếng Việt) và main slug chưa bị sửa tay
            let newMainSlug = prev.slug;
            if (field === 'title' && !isSlugManuallyEdited) {
                const firstLang = languages[0];
                if (firstLang && firstLang.id === langId) {
                    newMainSlug = slugify(value);
                }
            }

            return {
                ...prev,
                slug: newMainSlug,
                translation: updatedTranslations
            };
        });
    };

    const handleAutoGenerateSlug = () => {
        const viTrans = formData.translation.find(t => t.languageId === languages[0]?.id) || formData.translation[0];
        if (viTrans?.title) {
            setFormData(prev => ({ ...prev, slug: slugify(viTrans.title) }));
            setIsSlugManuallyEdited(false);
            toast.success('Đã tự động tạo slug từ tiêu đề');
        } else {
            toast.error('Vui lòng nhập tiêu đề trước khi tạo slug');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});

        if (!formData.slug && !formData.translation[0]?.title) {
            setErrors({ slug: 'Vui lòng nhập slug hoặc tiêu đề' });
            return;
        }

        const payload = {
            ...formData,
            sortOrder: Number(formData.sortOrder) || 0,
        };

        try {
            const res = id
                ? await updateMutation.mutateAsync({ id, data: payload })
                : await createMutation.mutateAsync(payload);

            if (res.success) {
                toast.success(id ? 'Cập nhật trang thành công' : 'Thêm mới trang thành công');
                navigate('/admin/custom-pages');
            } else {
                if (res.messageError) setErrors(res.messageError);
                else toast.error(res.message || 'Lưu thất bại');
            }
        } catch (err) {
            toast.error(err?.message || 'Lưu thất bại');
        }
    };

    const handlePreview = () => {
        if (!formData.slug) {
            toast.error('Trang chưa có đường dẫn slug để xem trước');
            return;
        }
        const cleanSlug = formData.slug.replace(/^\/+/, '');
        window.open(`/${cleanSlug}`, '_blank');
    };

    return (
        <div className="admin-page">
            <PageHeader
                title={id ? 'Cập nhật Trang' : 'Thêm Trang Mới'}
                subtitle="Nhập thông tin chi tiết bài viết, chính sách, điều khoản và cấu hình SEO"
                action={
                    id && (
                        <Button type="button" variant="secondary" onClick={handlePreview}>
                            <FiExternalLink style={{ marginRight: '6px' }} /> Xem trang công khai
                        </Button>
                    )
                }
            />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Cài đặt chung của Trang</p>
                <div className="form-row" style={{ alignItems: 'flex-start' }}>
                    <div style={{ flex: 2 }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <Input
                                    label="Đường dẫn tĩnh (Slug URL) *"
                                    name="slug"
                                    value={formData.slug}
                                    onChange={handleChange}
                                    placeholder="vd: chinh-sach-bao-mat"
                                    required
                                    error={errors.slug}
                                />
                            </div>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleAutoGenerateSlug}
                                title="Tự động sinh slug từ tiêu đề"
                                style={{ marginBottom: '16px', height: '42px', padding: '0 14px' }}
                            >
                                <FiRefreshCw />
                            </Button>
                        </div>
                        <small style={{ color: '#6c757d', marginTop: '-10px', display: 'block', marginBottom: '12px' }}>
                            Đường dẫn truy cập trang ngoài website sẽ là: <strong>/{formData.slug?.replace(/^\/+/, '') || 'slug-cua-trang'}</strong>
                        </small>
                    </div>
                    <div style={{ flex: 1 }}>
                        <Input
                            label="Thứ tự hiển thị"
                            name="sortOrder"
                            type="number"
                            value={formData.sortOrder}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <Checkbox
                        label="Kích hoạt / Hiển thị trang công khai"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleChange}
                    />
                </div>

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Nội dung theo Ngôn ngữ</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button
                                        key={lang.id}
                                        type="button"
                                        className={`tab-btn ${activeTab === i ? 'active' : ''}`}
                                        onClick={() => setActiveTab(i)}
                                    >
                                        {lang.name} ({lang.code})
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(tr => tr.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <div className="form-row">
                                            <div style={{ flex: 2 }}>
                                                <Input
                                                    label="Tiêu đề trang *"
                                                    value={t.title || ''}
                                                    onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)}
                                                    placeholder="VD: Chính sách bảo mật và xử lý dữ liệu"
                                                    required
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <Input
                                                    label="Slug riêng cho ngôn ngữ này (Tùy chọn)"
                                                    value={t.slug || ''}
                                                    onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)}
                                                    placeholder="vd: privacy-policy"
                                                />
                                            </div>
                                        </div>

                                        <Textarea
                                            label="Tóm tắt / Đoạn trích dẫn ngắn"
                                            value={t.summary || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'summary', e.target.value)}
                                            placeholder="Đoạn mô tả ngắn gọn về trang này..."
                                            rows={3}
                                        />

                                        <div className="input-group" style={{ marginTop: '1.2rem', marginBottom: '1.5rem' }}>
                                            <label style={{ marginBottom: '0.6rem', display: 'block', fontWeight: 600 }}>
                                                Nội dung chi tiết của trang *
                                            </label>
                                            <RichTextEditor
                                                value={t.content || ''}
                                                onChange={val => handleTranslationChange(lang.id, 'content', val)}
                                                placeholder="Nhập toàn bộ nội dung chính sách, quy định, hình ảnh hoặc các điều khoản tại đây..."
                                            />
                                        </div>

                                        <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', border: '1px solid #e9ecef', marginTop: '16px' }}>
                                            <strong style={{ display: 'block', marginBottom: '12px', color: '#495057' }}>Cấu hình SEO (Tùy chọn)</strong>
                                            <Input
                                                label="Tiêu đề SEO (Meta Title)"
                                                value={t.metaTitle || ''}
                                                onChange={e => handleTranslationChange(lang.id, 'metaTitle', e.target.value)}
                                                placeholder="Để trống sẽ dùng tiêu đề trang..."
                                            />
                                            <Textarea
                                                label="Mô tả SEO (Meta Description)"
                                                value={t.metaDescription || ''}
                                                onChange={e => handleTranslationChange(lang.id, 'metaDescription', e.target.value)}
                                                placeholder="Để trống sẽ dùng phần tóm tắt..."
                                                rows={2}
                                            />
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}

                <div className="form-actions" style={{ marginTop: '24px' }}>
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Đang lưu...' : id ? 'Cập nhật Trang' : 'Tạo Trang Mới'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/custom-pages')}>
                        Hủy
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default CustomPagesForm;
